import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { useApis } from '../contexts/ApiContext';
import { useAuth } from '../contexts/AuthContext';
import DiscountTaxManager from '../components/DiscountTaxManager';
import { API_URL } from '../utils/api';
import { FaEdit, FaTrash } from 'react-icons/fa';

const CreateEstimate = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams(); // For edit mode
  const { estimateApis } = useApis();
  const { user } = useAuth();

  // State from mobile app
  const [estimate, setEstimate] = useState(null);
  const [customer, setCustomer] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [removingProductId, setRemovingProductId] = useState(null);
  const hasCreatedRef = useRef(false);
  
  // Form state
  const [customerNote, setCustomerNote] = useState('');
  const [termsAndConditions, setTermsAndConditions] = useState('');
  const [orderNote, setOrderNote] = useState('');
  const [savedOrderNote, setSavedOrderNote] = useState('');
  const [orderNoteStatus, setOrderNoteStatus] = useState('idle'); // 'idle' | 'saving' | 'saved' | 'error'
  
  // Pricing state
  const [customerPrice, setCustomerPrice] = useState(0); // What dealer pays to admin
  const [customerProfit, setCustomerProfit] = useState(0); // Dealer's markup percentage
  
  // Get initial data from location state (for create mode)
  const initialData = location.state;
  
  // Function to refresh products when returning from AddProduct
  const refreshProducts = async () => {
    if (estimate && estimate.id && estimateApis) {
      try {
        const estimateData = await estimateApis.getEstimate(estimate.id);
        if (estimateData && estimateData.estimateData) {
          setProducts(estimateData.estimateData.products || []);
          console.log('✅ CreateEstimate: Products refreshed');
        }
      } catch (error) {
        console.error('❌ CreateEstimate: Error refreshing products:', error);
      }
    }
  };


  
  // Add effect to refresh products when coming back from AddProduct
  useEffect(() => {
    if (estimate && estimate.id) {
      refreshProducts();
    }
  }, [location.pathname, estimate]); // Refresh when the path changes (e.g., coming back from add-product)

  useEffect(() => {
    const fetchEstimate = async () => {
      if (id && estimateApis) {
        console.log('🔍 CreateEstimate: Loading estimate', id);
        setLoading(true);
        try {
          const estimateData = await estimateApis.getEstimate(id);
          console.log('📦 CreateEstimate: Estimate loaded:', estimateData);
          console.log('🔍 CreateEstimate: Estimate ID being fetched:', id);
          console.log('🔍 CreateEstimate: EstimateApis email:', estimateApis.email);
          
          if (estimateData && estimateData.id) {
            setEstimate(estimateData);
            if (estimateData.estimateData) {
              setCustomer(estimateData.estimateData.customer);
              setProducts(estimateData.estimateData.products || []);
            
              let customer_total = 0;
              estimateData.estimateData.products.forEach((product) => {
                customer_total += parseFloat(product.customer_item_total || 0);
              });
              setCustomerPrice(customer_total || 0);
              setCustomerProfit(estimateData.estimate_profit || 0);
              setCustomerNote(estimateData.notes?.customer_note || '');
              setTermsAndConditions(estimateData.notes?.terms_and_conditions || '');
              const loadedOrderNote = estimateData.notes?.order_note || '';
              setOrderNote(loadedOrderNote);
              setSavedOrderNote(loadedOrderNote);
            }
          } else {
            console.error('❌ CreateEstimate: Estimate not found with ID:', id);
            alert('Estimate not found. Please check if the estimate exists or try creating a new one.');
            navigate('/dashboard');
          }
        } catch (error) {
          console.error('❌ CreateEstimate: Error loading estimate:', error);
          alert('Error loading estimate: ' + error.message);
        } finally {
          setLoading(false);
        }
      } else if (initialData && !isCreating && !hasCreatedRef.current) {
        // Create mode - initialize with data from modal
        console.log('🔍 CreateEstimate: Create mode with data:', initialData);
        console.log('🔍 CreateEstimate: EstimateApis available:', !!estimateApis);
        
        // Prevent double creation (React StrictMode protection)
        if (isCreating || hasCreatedRef.current) {
          console.log('⚠️ CreateEstimate: Already creating or created, skipping...');
          return;
        }
        
        try {
          if (estimateApis) {
            setLoading(true);
            setIsCreating(true);
            hasCreatedRef.current = true; // Mark as created to prevent StrictMode double-creation
            console.log('🚀 CreateEstimate: Starting estimate creation...');
            
            const estimateId = await estimateApis.createEstimate({
              order_tag: initialData.orderTag,
              profit: 0
            });
            console.log('📦 CreateEstimate: New estimate created:', estimateId);
            // Redirect to edit mode with the new estimate ID
            navigate(`/edit-estimate/${estimateId}`, { replace: true });
          } else {
            console.error('❌ CreateEstimate: EstimateApis not available');
            alert('System not ready. Please wait a moment and try again.');
            navigate('/dashboard');
          }
        } catch (error) {
          console.error('❌ CreateEstimate: Error creating estimate:', error);
          alert('Error creating estimate: ' + error.message);
          navigate('/dashboard');
        } finally {
          setLoading(false);
          // Don't reset isCreating here to prevent re-attempts
        }
      }
    };

    fetchEstimate();
  }, [id, estimateApis, initialData, navigate]);

  const handleAddProduct = () => {
    if (!estimate || !estimate.estimateData?.customer?.id) {
      alert('Please select a customer first');
      return;
    }
    
    navigate('/add-product', {
      state: {
        estimate,
        customerId: estimate.estimateData.customer.id
      }
    });
  };

  const handleEditProduct = (product, index) => {
    if (!estimate || !estimate.estimateData?.customer?.id) {
      alert('Unable to edit product - missing customer information');
      return;
    }
    
    navigate('/add-product', {
      state: {
        estimate,
        customerId: estimate.estimateData.customer.id,
        editMode: true,
        productToEdit: product,
        productIndex: index
      }
    });
  };

  const handleRemoveProduct = async (product, index) => {
    if (!estimate || !estimate.estimateData?.customer?.id) {
      alert('Unable to remove product - missing customer information');
      return;
    }

    // Show confirmation dialog
    const productName = product.product?.name || 'this product';
    const confirmed = window.confirm(`Are you sure you want to remove "${productName}" from the cart?`);
    
    if (!confirmed) return;

    try {
      setRemovingProductId(product.id); // Set loading state
      
      const customerId = estimate.estimateData.customer.id;
      const cartEndpoint = estimate.orderID ? 'order-carts' : 'carts';
      const cartId = estimate.orderID || estimate.cartID;
      
      if (!product.id) {
        alert('Unable to remove product - missing product ID');
        return;
      }

      console.log(`🗑️ Removing product ${product.id} from ${cartEndpoint}/${cartId}`);

      const response = await fetch(`${API_URL}/customers/${customerId}/${cartEndpoint}/${cartId}/item/${product.id}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' }
      });

      const data = await response.json();

      if (data.success) {
        console.log('✅ Product removed successfully');
        
        // Remove product from local state immediately for better UX
        setProducts(prevProducts => prevProducts.filter((_, i) => i !== index));
        
        // Refresh products from server to ensure consistency
        await refreshProducts();
        
        alert('Product removed successfully!');
      } else {
        throw new Error(data.message || 'Failed to remove product');
      }
    } catch (error) {
      console.error('❌ Error removing product:', error);
      alert('Error removing product: ' + error.message);
    } finally {
      setRemovingProductId(null); // Clear loading state
    }
  };

  const handleSaveOrderNote = async () => {
    if (!estimate || !estimateApis) return;
    if (estimate.isConvertedToOrder || estimate.status === 'saved') return;
    if (!estimate.cartID) return;

    setOrderNoteStatus('saving');
    try {
      await estimateApis.updateEstimateOrderNote(
        estimate.cartID,
        estimate.id,
        orderNote
      );
      setSavedOrderNote(orderNote);
      setOrderNoteStatus('saved');
      setTimeout(() => {
        setOrderNoteStatus((status) => (status === 'saved' ? 'idle' : status));
      }, 2000);
    } catch (error) {
      console.error('❌ CreateEstimate: Error saving order note:', error);
      setOrderNoteStatus('error');
    }
  };

  const handleSave = async () => {
    if (!customer) {
      alert('Please add a customer before creating the order.');
      return;
    }

    if (products.length === 0) {
      alert('Please add products before creating the order.');
      return;
    }

    if (!estimate || !estimateApis) {
      alert('Estimate data not available. Please try again.');
      return;
    }

    try {
      setLoading(true);
      console.log('💾 CreateEstimate: Saving estimate...');
      
      await estimateApis.saveEstimate(
        0, // paymentReceived - default to 0
        customerNote,
        termsAndConditions,
        estimate.cartID,
        estimate.customerID,
        estimate.id,
        customerProfit,
        orderNote
      );

      console.log('✅ CreateEstimate: Estimate saved successfully');
      alert('Order created successfully!');
      navigate('/dashboard');
    } catch (error) {
      console.error('❌ CreateEstimate: Error saving estimate:', error);
      alert('Error creating order: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!estimate || !estimateApis) return;

    if (window.confirm('Are you sure you want to delete this draft estimate?')) {
      try {
        setLoading(true);
        console.log('🗑️ CreateEstimate: Deleting estimate...');
        
        await estimateApis.deleteEstimate(estimate.id);
        
        console.log('✅ CreateEstimate: Estimate deleted successfully');
        alert('Draft deleted successfully!');
        navigate('/dashboard');
      } catch (error) {
        console.error('❌ CreateEstimate: Error deleting estimate:', error);
        alert('Error deleting draft: ' + error.message);
      } finally {
        setLoading(false);
      }
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading estimate...</p>
        </div>
      </div>
    );
  }
  console.log("userr", user)

  if (!estimate) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Estimate Not Found</h2>
          <button
            onClick={() => navigate('/dashboard')}
            className="bg-blue-500 text-white px-6 py-2 rounded-lg hover:bg-blue-600"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto p-6">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h1 className="text-2xl font-bold text-gray-900">
              {id ? 'Edit Draft Order' : 'Create New Order'}
            </h1>
            <button
              onClick={() => navigate('/dashboard')}
              className="text-gray-500 hover:text-gray-700"
            >
              ✕
            </button>
          </div>
          
          {/* Order Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Order No.</label>
              <p className="text-lg font-semibold">#{String(estimate.estimateNo || 0).padStart(3, '0')}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Order Date</label>
              <p className="text-lg">{estimate.createdAt ? new Date(estimate.createdAt.seconds * 1000).toLocaleDateString() : 'N/A'}</p>
            </div>
          </div>
        </div>

        {/* Order Tag */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Order Information</h2>
          {customer ? (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Order Tag</label>
              <p className="text-lg font-medium text-blue-600">{customer.customer_name}</p>
            </div>
          ) : (
            <button 
              onClick={handleAddProduct}
              className="w-full border-2 border-dashed border-gray-300 rounded-lg p-4 text-center text-gray-500 hover:border-gray-400"
            >
              + Add Order Tag
            </button>
          )}
        </div>

        {/* Products Section */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Products & Measurements</h2>
            <button 
              onClick={handleAddProduct}
              className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 transition-colors duration-200"
            >
              + Add Product
            </button>
          </div>
          
          {products.length > 0 ? (
            <div className="space-y-4">
              {products.map((product, index) => {
                // Debug product data structure
                console.log('🔍 Product data structure:', product);
                console.log('🔍 Available price fields:', {
                  customer_item_total: product.customer_item_total,
                  item_total: product.item_total,
                  customerItemTotal: product.customerItemTotal,
                  itemTotal: product.itemTotal,
                  product_price: product.product_price,
                  customer_product_price: product.customer_product_price
                });

                // Get dimensions if available
                const width = product.inputs?.find(i => i.inputName === 'Width')?.value;
                const height = product.inputs?.find(i => i.inputName === 'Height')?.value;
                const roomName = product.inputs?.find(i => i.inputName === 'Room Name')?.value;
                
                // Handle different price field structures from API
                const productPrice = parseFloat(
                  product.customer_item_total || 
                  product.item_total || 
                  product.customerItemTotal ||
                  product.itemTotal ||
                  product.product_price ||
                  product.customer_product_price ||
                  0
                );
                
                console.log(`🔍 Final calculated price for product ${index}:`, productPrice);
                
                return (
                  <div key={index} className="border rounded-lg p-4 hover:shadow-md transition-shadow duration-200">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <h3 className="font-medium text-gray-900">{product.product?.name || 'Product'}</h3>
                        <div className="mt-1 space-y-1">
                          <p className="text-sm text-gray-600">
                            <span className="font-medium">Quantity:</span> {product.quantity || 1}
                          </p>
                          {roomName && (
                            <p className="text-sm text-gray-600">
                              <span className="font-medium">Room:</span> {roomName}
                            </p>
                          )}
                          {width && height && (
                            <p className="text-sm text-gray-600">
                              <span className="font-medium">Dimensions:</span> {width}" × {height}"
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-lg font-semibold text-gray-900">
                          ${productPrice.toFixed(2)}
                        </p>
                        <div className="flex space-x-3 mt-1">
                          <button 
                            onClick={() => handleEditProduct(product, index)}
                            className="flex items-center space-x-1 text-blue-500 text-sm hover:text-blue-700 transition-colors"
                            title="Edit product"
                          >
                            <FaEdit className="w-3 h-3" />
                            <span>Edit</span>
                          </button>
                          <button 
                            onClick={() => handleRemoveProduct(product, index)}
                            disabled={removingProductId === product.id}
                            className={`flex items-center space-x-1 text-sm transition-colors ${
                              removingProductId === product.id 
                                ? 'text-gray-400 cursor-not-allowed' 
                                : 'text-red-500 hover:text-red-700'
                            }`}
                            title={removingProductId === product.id ? "Removing..." : "Remove product"}
                          >
                            <FaTrash className="w-3 h-3" />
                            <span>{removingProductId === product.id ? 'Removing...' : 'Remove'}</span>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              No products added yet
            </div>
          )}
        </div>

        {/* Discount & Tax Section */}
        {/* {estimate && estimate.estimateData && products.length > 0 && (
          <DiscountTaxManager
            estimate={estimate}
            estimateApis={estimateApis}
            onRefresh={refreshEstimate}
          />
        )} */}

        {/* Pricing Section */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Pricing Summary</h2>
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Subtotal (Your Cost)</span>
              <span className="font-medium text-lg">${customerPrice.toFixed(2)}</span>
            </div>
            
            {/* Show discount if applied */}
            {/* {estimate?.estimateData?.discount > 0 && (
              <div className="flex justify-between items-center text-green-600">
                <span>Discount ({estimate.estimateData.discount_type === 'percentage' ? `${estimate.estimateData.discount}%` : `$${estimate.estimateData.discount}`})</span>
                <span>-${(estimate.estimateData.discount_type === 'percentage' 
                  ? customerPrice * (estimate.estimateData.discount / 100) 
                  : estimate.estimateData.discount).toFixed(2)}</span>
              </div>
            )} */}
            
            {/* Show tax if applied */}
            {estimate?.estimateData?.tax > 0 && (
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Tax ({estimate.estimateData.tax}%)</span>
                <span>+${((customerPrice - (estimate.estimateData.discount_type === 'percentage' 
                  ? customerPrice * (estimate.estimateData.discount / 100) 
                  : estimate.estimateData.discount || 0)) * (estimate.estimateData.tax / 100)).toFixed(2)}</span>
              </div>
            )}
            
            {/* Total Cost (what dealer pays to store) - always show */}
            <div className="flex justify-between items-center py-3 bg-green-50 px-4 rounded-lg border-l-4 border-green-500">
              <span className="font-medium text-gray-900">
                Total Cost (will be paid to {user?.storeData?.store_name || 'Store'})
              </span>
              <span className="font-semibold text-lg text-green-700">
                ${(() => {
                  const discountAmount = estimate?.estimateData?.discount_type === 'percentage'
                    ? customerPrice * (estimate.estimateData.discount / 100)
                    : estimate.estimateData.discount || 0;
                  const subtotalAfterDiscount = customerPrice - discountAmount;
                  const taxAmount = estimate?.estimateData?.tax > 0
                    ? subtotalAfterDiscount * (estimate.estimateData.tax / 100)
                    : 0;
                  return (subtotalAfterDiscount + taxAmount).toFixed(2);
                })()}
              </span>
            </div>
          </div>
        </div>

        {/* Order Note */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Ship To</h2>
            {orderNoteStatus === 'saving' && (
              <span className="text-xs text-gray-500">Saving…</span>
            )}
            {orderNoteStatus === 'saved' && (
              <span className="text-xs text-green-600">Saved</span>
            )}
            {orderNoteStatus === 'error' && (
              <span className="text-xs text-red-600">Failed to save</span>
            )}
          </div>
          <textarea
            value={orderNote}
            onChange={(e) => setOrderNote(e.target.value)}
            rows={3}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Add notes for this order (e.g. delivery instructions, internal context)..."
          />
          <div className="flex justify-end mt-3">
            <button
              type="button"
              onClick={handleSaveOrderNote}
              disabled={orderNoteStatus === 'saving' || orderNote === savedOrderNote}
              className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
            >
              {orderNoteStatus === 'saving' ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>

        {/* Notes Section */}
        {/* <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Notes</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Customer Note</label>
              <textarea
                value={customerNote}
                onChange={(e) => setCustomerNote(e.target.value)}
                rows={3}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Thank you for your business!"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Terms & Conditions</label>
              <textarea
                value={termsAndConditions}
                onChange={(e) => setTermsAndConditions(e.target.value)}
                rows={3}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter terms and conditions..."
              />
            </div>
          </div>
        </div> */}

        {/* Action Buttons */}
        {estimate.status === 'draft' && (
          <div className="flex space-x-4">
            <button
              onClick={handleSave}
              disabled={loading}
              className="flex-1 bg-blue-500 text-white py-3 px-6 rounded-lg hover:bg-blue-600 disabled:opacity-50"
            >
              {loading ? 'Creating Order...' : 'Create Order'}
            </button>
            <button
              onClick={handleDelete}
              disabled={loading}
              className="flex-1 bg-red-500 text-white py-3 px-6 rounded-lg hover:bg-red-600 disabled:opacity-50"
            >
              {loading ? 'Deleting...' : 'Delete Draft'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default CreateEstimate; 